import { useUiLanguage } from "@/i18n/useUiLanguage";
import { systemText, tr, uiLocale } from "@/i18n/copy";
import { currencyLabel } from "@/lib/money";
import {toast} from 'sonner';
import SettingsSection from '@/components/SettingsSection';
import {useEffect,useRef,useState} from 'react';
import {CheckCircle2, CircleAlert, LoaderCircle} from 'lucide-react';
import {api,errMsg} from '@/lib/api';
import {createRefreshQueue} from '@/lib/refreshQueue';
const field='min-h-12 w-full rounded-xl border bg-background px-3 text-foreground';
function clearStripeReturn() {
  const url = new URL(window.location.href);
  url.searchParams.delete('deposit_stripe');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

export default function DepositSettings(){
  useUiLanguage();
  const panelRef=useRef(null);
  const [stripeReturn] = useState(() => new URLSearchParams(window.location.search).get('deposit_stripe') === 'return');
  const [form,setForm]=useState(null),[busy,setBusy]=useState(stripeReturn),[message,setMessage]=useState('');
  const [stripeCheck,setStripeCheck]=useState(stripeReturn ? 'checking' : null);
  const [messageError,setMessageError]=useState(false);
  const load=()=>api.get('/studio/deposit-settings').then(setForm);
  useEffect(()=>{
    let active=true;
    const refresh=async()=>{
      if(stripeReturn){
        try {
          const result=await api.post('/studio/deposit-stripe/refresh');
          if(!active)return;
          setStripeCheck(result.ready === true ? 'ready' : 'incomplete');
          clearStripeReturn();
          if(result.ready === true)toast.success(tr("Stripe pronto a ricevere acconti."));
        }catch(e){
          if(!active)return;
          setStripeCheck('error');setMessage(errMsg(e));setMessageError(true);
        }
      }
      try {
        const data=await api.get('/studio/deposit-settings');
        if(active)setForm(data);
      }catch(e){if(active){setMessage(errMsg(e));setMessageError(true);}}
      finally{if(active)setBusy(false);}
    };
    refresh();return()=>{active=false;};
  },[stripeReturn]);
  async function action(kind,event){
    const panel=event?.currentTarget.closest("details");
    setBusy(true);setMessage('');setMessageError(false);
    if(kind==='refresh')setStripeCheck('checking');
    try{
      if(kind==='save'){
        await api.put('/studio/deposit-settings',form);
        setMessage(tr("Metodi di pagamento salvati."));
        if(panel)panel.open=false;panelRef.current?.close();
        toast.success(tr("Metodi di pagamento salvati"));
        await load();
      }else{
        const result=await api.post(`/studio/deposit-stripe/${kind}`);
        if(result.url){window.location.assign(result.url);return;}
        const ready=result.ready === true;
        setStripeCheck(ready ? 'ready' : 'incomplete');
        setForm(current=>current ? {...current,stripe_ready:ready} : current);
        clearStripeReturn();
        if(ready)toast.success(tr("Stripe pronto a ricevere acconti."));
      }
    }catch(e){
      if(kind==='refresh')setStripeCheck('error');
      setMessage(errMsg(e));setMessageError(true);
    }finally{setBusy(false);}
  }
  const status=stripeCheck || (form ? (form.stripe_ready ? 'ready' : form.stripe_connected ? 'incomplete' : 'unconfigured') : 'loading');
  const ready=status==='ready', checking=status==='checking'||status==='loading';
  const summaryStatus=ready ? <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300"><CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0"/>{tr("Pagamenti online abilitati")}</span> : null;
  return <SettingsSection panelRef={panelRef} id="deposits" defaultOpen={stripeReturn||window.location.hash==='#deposits'} status={summaryStatus} title={tr("Come ricevi gli acconti")} subtitle={tr("Stripe, bonifico istantaneo e pagamento in studio")}><p>{tr("Il cliente deve versare entro 24 ore dall’accettazione del preventivo. L’importo viene inserito nella singola proposta.")}</p>
    {form&&<><label className="flex gap-3"><input type="checkbox" checked={!!form.bank_enabled} onChange={e=>setForm({...form,bank_enabled:e.target.checked})}/>{tr("Bonifico istantaneo")}</label>{form.bank_enabled&&<><label className="block">{tr("Intestatario")}<input className={field} value={form.account_holder||''} onChange={e=>setForm({...form,account_holder:e.target.value})}/></label><label className="block">{tr("IBAN")}<input className={field} value={form.iban||''} onChange={e=>setForm({...form,iban:e.target.value})}/></label><p className="text-sm">{tr("Solo bonifico istantaneo. Verifica l’accredito: la ricevuta del cliente non conferma la prenotazione.")}</p></>}
    <label className="flex gap-3"><input type="checkbox" checked={form.in_studio!==false} onChange={e=>setForm({...form,in_studio:e.target.checked})}/>{tr("Pagamento in studio entro 24 ore")}</label><label className="block">{tr("Istruzioni per il cliente")}<textarea className={field} maxLength={1000} value={form.instructions||''} onChange={e=>setForm({...form,instructions:e.target.value})}/></label><button disabled={busy} onClick={e=>action('save',e)} className="min-h-12 rounded-xl bg-primary px-4 text-primary-foreground">{tr("Salva metodi")}</button></>}
    <div className="space-y-3 border-t pt-4">
      <h3 className="font-semibold">{tr("Stripe dello Studio")}</h3>
      <div role="status" aria-live="polite" data-testid="stripe-connection-status" data-state={status} className={`flex items-start gap-3 rounded-xl border p-4 ${ready ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200' : 'border-border bg-muted/20'}`}>
        {ready ? <CheckCircle2 aria-hidden="true" className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600 dark:text-emerald-400"/> : checking ? <LoaderCircle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 animate-spin"/> : <CircleAlert aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400"/>}
        <div><p className="font-semibold">{ready ? tr("Configurazione completata") : checking ? tr("Verifica in corso…") : status==='unconfigured' ? tr("Non configurato") : tr("Configurazione da completare o verificare")}</p>
          {ready&&<p className="mt-1 text-sm">{tr("Stripe pronto a ricevere acconti.")}</p>}
          {status==='incomplete'&&<p className="mt-1 text-sm">{tr("Completa la configurazione Stripe per ricevere pagamenti.")}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2"><button disabled={busy||!form} className="min-h-10 rounded-xl bg-primary px-4 py-2 font-semibold text-primary-foreground disabled:opacity-50" onClick={()=>action('connect')}>{tr("Configura Stripe")}</button>
      {(form?.stripe_connected||stripeReturn)&&<button disabled={busy} className="min-h-10 rounded-xl bg-secondary px-4 py-2 font-semibold text-secondary-foreground disabled:opacity-50" onClick={()=>action('refresh')}>{status==='checking' ? tr("Verifica in corso…") : tr("Verifica collegamento")}</button>}</div>
    </div>
    {message&&<p role={messageError ? "alert" : "status"} className={messageError ? "text-destructive" : "text-sm"}>{message}</p>}
  </SettingsSection>;
}

export function ClientDepositPayment({relationshipId,appointmentId,currency="EUR"}){
  useUiLanguage();
  const [data,setData]=useState(null),[error,setError]=useState(''),[busy,setBusy]=useState(false);
  const url=`/client/relationships/${relationshipId}/appointments/${appointmentId}`;
  useEffect(()=>{
    let loaded=false, latest=null;
    setData(null);setError('');
    const queue=createRefreshQueue(async isActive=>{
      try{
        const result=await api.get(url+'/deposit-options');
        if(!isActive())return;
        loaded=true;latest=result;setData(result);setError('');
      }catch(e){if(isActive())setError(errMsg(e));}
    });
    const refresh=()=>{if(document.visibilityState!=='hidden')queue.refresh();};
    refresh();
    // Settled deposits need event/focus refreshes, not a permanent polling loop.
    const timer=setInterval(()=>{if(!loaded||latest?.requirement?.status==='pending')refresh();},30000);
    const events=['focus','online','tps:data-changed'];
    events.forEach(event=>window.addEventListener(event,refresh));
    document.addEventListener('visibilitychange',refresh);
    return()=>{
      queue.stop();clearInterval(timer);
      events.forEach(event=>window.removeEventListener(event,refresh));
      document.removeEventListener('visibilitychange',refresh);
    };
  },[url]);
  async function pay(){setBusy(true);setError('');try{const r=await api.post(url+'/deposit-checkout');window.location.assign(r.url);}catch(e){setError(errMsg(e));setBusy(false);}}
  const req=data?.requirement;
  if(!req)return error?<p role="alert">{systemText(error)}</p>:null;
  const pending=req.status==='pending';
  return <section className="my-3 space-y-3 rounded-xl border border-primary/40 p-4"><h3 className="font-semibold">{({pending:tr("Acconto da versare"),paid:tr("Acconto verificato · prenotazione confermata"),expired:tr("Termine per l’acconto scaduto"),paid_needs_reschedule:tr("Acconto ricevuto · contatta lo Studio per concordare la data")})[req.status]}</h3><p>{tr("Acconto richiesto:")} {currencyLabel(data, currency)} {Number(req.amount).toFixed(2)} {tr("· Ricevuto:")} {currencyLabel(data, currency)} {Number(data.received).toFixed(2)}</p><p>{tr("Versamento entro")} {new Date(req.deadline).toLocaleString(uiLocale())}</p>
    {pending&&<><p>{tr("La prenotazione diventa ufficiale dopo la verifica dell’acconto. Scegliere un metodo non prolunga la scadenza.")}</p>{data.stripe&&<button disabled={busy} onClick={pay} className="min-h-12 rounded-xl bg-primary px-4 text-primary-foreground">{busy?tr("Apertura pagamento…"):tr("Paga con Stripe")}</button>}
    {data.bank&&<details className="rounded-xl border p-3"><summary className="min-h-10 cursor-pointer font-semibold">{tr("Bonifico esclusivamente istantaneo")}</summary><p>{tr("Intestatario:")} {data.bank.account_holder}</p><p className="break-all select-all">{tr("IBAN:")} {data.bank.iban}</p><p className="break-all select-all">{tr("Causale:")} {data.bank.reference}</p><p className="mt-2">{tr("Non usare un bonifico ordinario. L’accredito deve arrivare entro la scadenza. Lo Studio verifica il versamento.")}</p></details>}
    {data.in_studio&&<div className="rounded-xl border p-3"><b>{tr("Passa in studio")}</b><p>{tr("Versa l’acconto entro la stessa scadenza, con uno dei metodi accettati in sede. Lo Studio registra l’incasso.")}</p></div>}{data.instructions&&<p>{data.instructions}</p>}</>}{error&&<p role="alert">{systemText(error)}</p>}
  </section>;
}



